import { RouteData } from '@cya-routes/shared'

/**
 * Calculate bounds for an array of coordinates
 */
export function calculateBounds(coordinates: [number, number][]): [[number, number], [number, number]] {
  // Filter valid coordinates
  const validCoords = coordinates.filter(coord => {
    if (!coord || !Array.isArray(coord) || coord.length < 2) return false
    const [lng, lat] = coord
    return typeof lng === 'number' && typeof lat === 'number' &&
           !isNaN(lng) && !isNaN(lat) &&
           isFinite(lng) && isFinite(lat) &&
           lng >= -180 && lng <= 180 &&
           lat >= -90 && lat <= 90
  })
  
  if (validCoords.length === 0) {
    // Return default bounds (Melbourne) if no valid coordinates
    return [[144.9631 - 0.1, -37.8136 - 0.1], [144.9631 + 0.1, -37.8136 + 0.1]]
  }
  
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity

  validCoords.forEach(([lng, lat]) => {
    minLng = Math.min(minLng, lng)
    minLat = Math.min(minLat, lat)
    maxLng = Math.max(maxLng, lng)
    maxLat = Math.max(maxLat, lat)
  })

  const padding = 0.01
  return [
    [minLng - padding, minLat - padding],
    [maxLng + padding, maxLat + padding]
  ]
}

/**
 * Function to find coordinates at a specific distance along the route
 */
export function getCoordinatesAtDistance(route: RouteData, targetDistance: number): [number, number] | null {
  if (!route.coordinates || route.coordinates.length === 0) return null
  
  // If we have elevation profile data with distances, use it for accuracy
  if (route.elevation?.profile && route.elevation.profile.length > 0) {
    const elevationProfile = route.elevation.profile
    
    // Find the two points that bracket our target distance
    let index1 = 0
    let index2 = 1
    
    for (let i = 0; i < elevationProfile.length - 1; i++) {
      if (elevationProfile[i].distance <= targetDistance && elevationProfile[i + 1].distance >= targetDistance) {
        index1 = i
        index2 = i + 1
        break
      }
    }
    
    // Handle edge case where we're past the end
    if (targetDistance >= elevationProfile[elevationProfile.length - 1].distance) {
      index1 = elevationProfile.length - 2
      index2 = elevationProfile.length - 1
    }
    
    // Calculate interpolation ratio
    const dist1 = elevationProfile[index1].distance
    const dist2 = elevationProfile[index2].distance
    const segmentDist = dist2 - dist1
    const distInSegment = targetDistance - dist1
    const ratio = segmentDist > 0 ? Math.max(0, Math.min(1, distInSegment / segmentDist)) : 0
    
    // Get the corresponding coordinate indices
    const coordIndex1 = Math.floor((index1 / elevationProfile.length) * route.coordinates.length)
    const coordIndex2 = Math.min(coordIndex1 + 1, route.coordinates.length - 1)
    
    const [lng1, lat1] = route.coordinates[coordIndex1]
    const [lng2, lat2] = route.coordinates[coordIndex2]
    
    const lng = lng1 + (lng2 - lng1) * ratio
    const lat = lat1 + (lat2 - lat1) * ratio
    
    return [lng, lat]
  }
  
  // Fallback: Use coordinate distances if no elevation profile
  const coordinateDistances = route.coordinateDistances || []
  
  // Find the two points that bracket our target distance
  let index1 = 0
  let index2 = 1
  
  for (let i = 0; i < coordinateDistances.length - 1; i++) {
    if (coordinateDistances[i] <= targetDistance && coordinateDistances[i + 1] >= targetDistance) {
      index1 = i
      index2 = i + 1
      break
    }
  }
  
  // Handle edge case where we're past the end
  if (targetDistance >= coordinateDistances[coordinateDistances.length - 1]) {
    index1 = route.coordinates.length - 2
    index2 = route.coordinates.length - 1
  }
  
  // Calculate interpolation ratio
  const segmentDist = coordinateDistances[index2] - coordinateDistances[index1]
  const distInSegment = targetDistance - coordinateDistances[index1]
  const ratio = segmentDist > 0 ? Math.max(0, Math.min(1, distInSegment / segmentDist)) : 0
  
  const [lng1, lat1] = route.coordinates[index1]
  const [lng2, lat2] = route.coordinates[index2]
  
  const lng = lng1 + (lng2 - lng1) * ratio
  const lat = lat1 + (lat2 - lat1) * ratio
  
  return [lng, lat]
}